/**
 * Market Detail Modal Component
 * Displays market details in view-only mode
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '../../config/api';
import { useAuth } from '../../store/AuthContext';
import { apiService } from '../../services/apiService';
import PulseSourcesSection from '../PulseSourcesSection';
import './MarketDetailModal.css';

interface Approval {
  id: string;
  admin_id: string;
  approved: boolean;
  approved_at: string | null;
  admin_name?: string;
  admin_email?: string;
}

interface MarketDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReject?: () => void;
  market: {
    id: string;
    headline: string;
    description: string;
    category: string;
    answer_type: string;
    answer_options: string[] | null;
    min_stake: number;
    max_stake: number;
    end_time_type: string;
    duration_value: number | null;
    duration_unit: string | null;
    end_date: string | null;
    payout_type: string;
    payout_after: string | null;
    payout_date: string | null;
    image_url: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    settled_at: string | null;
    created_by?: string | null;
  } | null;
  onApprove?: () => void;
}

const MarketDetailModal: React.FC<MarketDetailModalProps> = ({ isOpen, onClose, market, onApprove, onReject }) => {
  const { admin } = useAuth();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadApprovals = useCallback(async () => {
    if (!market?.id) return;
    setLoading(true);
    try {
      const response = await apiService.getMarketApprovals(market.id);
      setApprovals(response.approvals || []);
    } catch (err) {
      console.error('Failed to load approvals:', err);
    } finally {
      setLoading(false);
    }
  }, [market?.id]);

  useEffect(() => {
    if (isOpen && market?.id) {
      loadApprovals();
    }
  }, [isOpen, market?.id, loadApprovals]);

  const handleApprove = async () => {
    if (!market?.id || approving) return;
    
    setApproving(true);
    try {
      await apiService.approveMarket(market.id);
      await loadApprovals();
      if (onApprove) {
        onApprove();
      }
    } catch (err) {
      console.error('Failed to approve market:', err);
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!market?.id || rejecting) return;
    
    setRejecting(true);
    try {
      await apiService.rejectMarket(market.id);
      if (onReject) {
        await onReject();
      }
      onClose();
    } catch (err) {
      console.error('Failed to reject market:', err);
    } finally {
      setRejecting(false);
    }
  };

  const isSuperAdmin = (): boolean => admin?.role === 'master';
  const getRequiredApprovals = (): number => (isSuperAdmin() ? 1 : 2);
  const canReject = (): boolean => isSuperAdmin();

  const getApprovalCount = (): number => {
    return approvals.filter(a => a.approved).length;
  };

  const isFullyApproved = (): boolean => {
    return getApprovalCount() >= getRequiredApprovals();
  };

  const hasCurrentAdminApproved = (): boolean => {
    if (!admin) return false;
    return approvals.some(a => a.admin_id === admin.id && a.approved);
  };

  const isMarketCreator = (): boolean => {
    if (!admin) return false;
    return market?.created_by === admin.id;
  };

  const canApprove = (): boolean => {
    if (!admin || !market) return false;
    if (!isSuperAdmin() && isMarketCreator()) return false;
    if (hasCurrentAdminApproved()) return false;
    if (isFullyApproved()) return false;
    return true;
  };

  if (!isOpen || !market) return null;

  const approvalCount = getApprovalCount();

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getAnswerTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      yesno: 'Yes/No',
      multiple: 'Multiple Choice',
      freetype: 'Free Type',
      percentage: 'Percentage',
      number: 'Number',
      date: 'Date',
    };
    return labels[type] || type;
  };

  const getEndTimeDisplay = () => {
    if (market.end_time_type === 'duration') {
      return `${market.duration_value} ${market.duration_unit || 'Days'}`;
    } else if (market.end_date) {
      return formatDate(market.end_date);
    }
    return 'N/A';
  };

  const getPayoutDisplay = () => {
    if (market.payout_type === 'after') {
      return market.payout_after || 'After end';
    } else if (market.payout_date) {
      return formatDate(market.payout_date);
    }
    return 'N/A';
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'live':
        return 'status-badge status-live';
      case 'draft':
        return 'status-badge status-draft';
      case 'settled':
        return 'status-badge status-settled';
      case 'cancelled':
        return 'status-badge status-cancelled';
      default:
        return 'status-badge';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content market-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Market Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {market.image_url && (
            <div className="detail-section">
              <div className="image-preview-container">
                <img 
                  src={
                    market.image_url?.startsWith('http')
                      ? market.image_url
                          .replace('http://localhost:3001', API_CONFIG.baseURL)
                          .replace('http://localhost:3000', API_CONFIG.baseURL)
                      : `${API_CONFIG.baseURL}${market.image_url}`
                  }
                  alt={market.headline}
                  className="market-image-preview"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-row">
              <span className="detail-label">Status</span>
              <span className={getStatusClass(market.status)}>{market.status}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Headline</span>
              <span className="detail-value">{market.headline}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Description</span>
              <span className="detail-value">{market.description}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Category</span>
              <span className="detail-value">{market.category}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Answer Type</span>
              <span className="detail-value">{getAnswerTypeLabel(market.answer_type)}</span>
            </div>

            {market.answer_options && market.answer_options.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">Answer Options</span>
                <div className="detail-value">
                  <ul className="options-list">
                    {market.answer_options.map((option, index) => (
                      <li key={index}>{option}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            <div className="detail-row">
              <span className="detail-label">Stake Range</span>
              <span className="detail-value">£{market.min_stake} - £{market.max_stake}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">End Time</span>
              <span className="detail-value">{getEndTimeDisplay()}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Payout Timing</span>
              <span className="detail-value">{getPayoutDisplay()}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">{formatDate(market.created_at)}</span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">{formatDate(market.updated_at)}</span>
            </div>

            {market.settled_at && (
              <div className="detail-row">
                <span className="detail-label">Settled At</span>
                <span className="detail-value">{formatDate(market.settled_at)}</span>
              </div>
            )}
          </div>

          {/* Approval Status Section */}
          {market.status === 'pending' && (
            <div className="detail-section">
              <div className="detail-row">
                <span className="detail-label">Approval Status</span>
                <div className="detail-value">
                  <div className={`approval-status ${isFullyApproved() ? 'approved' : ''}`} style={{ marginBottom: '20px' }}>
                    <span style={{ fontSize: '20px' }}>{isFullyApproved() ? '✅' : '⏳'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>Approval Status: {approvalCount}/{getRequiredApprovals()}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {isSuperAdmin() ? 'Super admin: 1 approval publishes' : 'Requires 2 admin approvals'}
                      </div>
                    </div>
                  </div>
                  {approvals.length > 0 && (
                    <div className="approval-by-section">
                      {approvals.map((approval, idx) => (
                        <div key={idx} className={`approval-by-item ${approval.approved ? '' : 'pending'}`}>
                          <div className="approval-icon">{approval.approved ? '✅' : '⏳'}</div>
                          <div className="approval-content">
                            <div className="approval-name">{approval.admin_name || 'Admin'}</div>
                            <div className="approval-meta">
                              {approval.approved ? 'Approved' : 'Pending'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {(canApprove() || canReject()) && (
                    <div style={{ marginTop: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      {canApprove() && (
                        <button
                          className="btn btn-success"
                          onClick={handleApprove}
                          disabled={approving || rejecting}
                          style={{ backgroundColor: '#22c55e', color: 'white', border: 'none' }}
                        >
                          {approving ? 'Accepting...' : '✓ Accept'}
                        </button>
                      )}
                      {canReject() && (
                        <button
                          className="btn btn-danger"
                          onClick={handleReject}
                          disabled={approving || rejecting}
                          style={{ backgroundColor: '#ef4444', color: 'white', border: 'none' }}
                        >
                          {rejecting ? 'Rejecting...' : '✗ Reject'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Approval History Section for Live Markets */}
          {market.status === 'live' && (
            <div className="detail-section">
              <div className="detail-row detail-row-approved-by">
                <span className="detail-label">Approved By</span>
                <div className="detail-value">
                  {loading ? (
                    <div style={{ color: 'var(--text-muted)' }}>Loading approvals...</div>
                  ) : approvals.length > 0 ? (
                    <div className="approval-by-section">
                      {approvals
                        .filter(a => a.approved)
                        .map((approval, idx) => (
                          <div key={idx} className="approval-by-item">
                            <div className="approval-icon">✅</div>
                            <div className="approval-content">
                              <div className="approval-name">{approval.admin_name || 'Admin'}</div>
                              <div className="approval-meta">
                                Approved {approval.approved_at ? formatDate(approval.approved_at) : 'N/A'}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div style={{ color: 'var(--text-muted)' }}>No approval information available</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Pulse Sources Section — attach tweet / reddit URL to this market */}
          <PulseSourcesSection betId={market.id} />
        </div>
      </div>
    </div>
  );
};

export default MarketDetailModal;
